import { OrderUnitOfMeasure } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { isNil } from 'lodash-es';

import { unitOfMeasureOptions as allUnitOfMeasureOptions } from '~constants/enums';
import { MaterialRateTypeahead } from '~hooks/useMaterialRates/models';

/**
 * Label formatting for the two material-rate surfaces on the order form: the
 * editable rate dropdown inside the primary material card, and the read-only
 * project-rate lines printed under each additive material row directly beneath
 * it. Both stack in the same card, so they have to spell money and the rate's
 * site identically — those two conventions live here and nowhere else.
 */

const RATE_LABEL_PART_SEPARATOR = ' - ';

// Every surface marks the site a rate is scoped to in trailing parentheses.
function parenthesizeSite(siteName: string) {
  return `(${siteName})`;
}

// A material rate's unit arrives as the raw API value ("ton"); the shared option
// list carries the display spelling for it.
export function materialRateUnitOfMeasureLabel(unitOfMeasure: string) {
  return (
    allUnitOfMeasureOptions.find((option) => option.id === unitOfMeasure)?.name ??
    unitOfMeasure
  );
}

/**
 * The rate-dropdown option label. Leads with the material because the dropdown
 * is how a user picks between materials.
 */
export function makeMaterialRateLabel(
  materialRate: MaterialRateTypeahead,
  currencyFormatter: (input: string | number) => string,
) {
  const materialName = materialRate.material?.name || '';
  const externalId = materialRate.material?.externalId || '';
  const price = isNil(materialRate.sellPrice)
    ? ''
    : currencyFormatter(materialRate.sellPrice);
  const site = materialRate.site?.name || '';

  const parts = [materialName, externalId, price];
  if (site) {
    parts.push(parenthesizeSite(site));
  }

  return parts.filter(Boolean).join(RATE_LABEL_PART_SEPARATOR);
}

type MaterialRateCardLineParts = {
  buyPrice: number | null;
  sellPrice: number | null;
  siteName: string | null;
  unitOfMeasure: OrderUnitOfMeasure;
};

/**
 * One read-only project-rate line for a material row. Omits the material name —
 * the row already has it as a field — and shows both sides of the rate, because
 * on a hauler-side order the buy price is what the company itself pays.
 *
 * Returns null for a rate that carries neither price: there is nothing to read
 * off such a line, so the row is better off without it.
 */
export function makeMaterialRateCardLine(
  { buyPrice, sellPrice, siteName, unitOfMeasure }: MaterialRateCardLineParts,
  currencyFormatter: (input: string | number) => string,
) {
  const unit = materialRateUnitOfMeasureLabel(unitOfMeasure);
  const parts: string[] = [];

  // The two prices are independent on the record, so each is emitted only when
  // it is actually set — a missing side must not print as $0.00.
  if (!isNil(buyPrice)) {
    parts.push(
      t('order.form.project_material_rate_buy', {
        price: currencyFormatter(buyPrice),
        unit,
      }),
    );
  }

  if (!isNil(sellPrice)) {
    parts.push(
      t('order.form.project_material_rate_sell', {
        price: currencyFormatter(sellPrice),
        unit,
      }),
    );
  }

  if (parts.length === 0) {
    return null;
  }

  // A rate with no site is the project-wide one. Naming that explicitly keeps a
  // sited rate and an unscoped one distinguishable when both are listed.
  parts.push(
    parenthesizeSite(siteName ?? t('order.form.project_material_rate_all_sites')),
  );

  return parts.join(RATE_LABEL_PART_SEPARATOR);
}
